import { numberCitations, type CitationNumbering } from "#features/citations/citedContent.ts";
import { useCitedContentHelpers, type CitedConversation } from "#features/conversations/hooks/useMessageContent.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { presentPersonProfileSections } from "#types/PersonProfile.ts";
import { useMemo, useRef } from "react";

interface Options {
    /** Numbers the person profile card alone, for a card replayed without the reply above it. */
    isProfileOnly?: boolean;
}

// The answer's text in reading order: the reply, then the person profile card rendered under it,
// with only the sections the card renders.
const getCitingText = ({ content, personProfile }: ConversationMessageModel, isProfileOnly: boolean) => [
    isProfileOnly ? null : content,
    personProfile?.intro,
    ...presentPersonProfileSections(personProfile?.sections ?? []).map(({ body }) => body)
].filter(Boolean).join("\n\n");

const isSameNumbering = (previous: CitationNumbering, next: CitationNumbering) =>
    previous.numbersByIndex.size === next.numbersByIndex.size
    && [...next.numbersByIndex].every(([citationIndex, displayNumber]) => previous.numbersByIndex.get(citationIndex) === displayNumber)
    && previous.citations.length === next.citations.length
    && next.citations.every((citation, position) => citation.source === previous.citations[position].source && citation.displayNumber === previous.citations[position].displayNumber);

/**
 * The answer's citations, numbered 1..N in reading order, resolved the way its markers are (across
 * the conversation), so a pill, the sources chip and the sources modal agree on every number.
 */
export const useMessageCitations = (cited: CitedConversation, message: ConversationMessageModel, { isProfileOnly = false }: Options = {}): CitationNumbering => {
    const { getSourceById } = useCitedContentHelpers(cited);
    const citingText = getCitingText(message, isProfileOnly);
    const numberingRef = useRef<CitationNumbering | null>(null);

    // The same numbering while a streamed reply only adds text: what's built on it (the markdown's link component) then
    // stays one component across the reply's chunks, rather than remounting its pills and person chips with each one.
    return useMemo(() => {
        const numbering = numberCitations(citingText, getSourceById);
        const previous = numberingRef.current;
        if (previous && isSameNumbering(previous, numbering)) {
            return previous;
        }
        numberingRef.current = numbering;

        return numbering;
    }, [citingText, getSourceById]);
};
